import { RowSelectionState } from "@tanstack/react-table";
import { FormEvent, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { HouseholdGroupMember } from "../../../../api/src/household-groups/household-groups.service";
import ActionButton from "../../components/ActionButton";
import SubmitButton from "../../components/SubmitButton";
import { serverError } from "../../lib/api";
import { toISODateNoTimezone } from "../../lib/date";
import HouseholdGroupHouseholdPicker from "./HouseholdGroupHouseholdPicker";
import {
  useAddHouseholdGroupMember,
  useChangeHouseholdGroupMemberEffectiveDate,
  useNotifyHouseholdGroupUpdated,
  useRemoveHouseholdGroupMember,
} from "./lib";

/**
 * The api's refusal text when it sent one, else the app's generic text. No
 * other words are added to the api's text.
 */
const failureText = (err: unknown): string => {
  const message = serverError(err)?.body?.message;
  return typeof message === "string" ? message : "An error occurred";
};

const today = () => toISODateNoTimezone(new Date());

/**
 * Adds one household from an effective date. A household that is a member of
 * another group on any date of the new membership is not in the picker (the
 * picker's rule). It mounts only while its modal is shown, so no request
 * leaves while the modal is closed.
 */
const AddHouseholdGroupMemberForm = ({
  householdGroupId,
  onSaved,
  onHide,
}: {
  householdGroupId: number;
  /** Called after the api accepts the write. */
  onSaved: () => void;
  onHide: () => void;
}) => {
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const addMember = useAddHouseholdGroupMember(householdGroupId);

  const selectedHouseholdId = Object.keys(rowSelection).find(
    (householdId) => rowSelection[householdId],
  );

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (selectedHouseholdId === undefined) {
      return;
    }
    setSubmitError(null);
    try {
      await addMember.mutateAsync({
        householdId: Number(selectedHouseholdId),
        effectiveDate,
      });
      onSaved();
      onHide();
    } catch (err) {
      console.error("Failed to add household to household group", err);
      setSubmitError(failureText(err));
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <Modal.Body>
        {submitError === null ? null : (
          <Alert variant="danger">{submitError}</Alert>
        )}
        <Form.Group
          className="mb-3"
          controlId="household-group-add-effectiveDate"
        >
          <Form.Label>Effective Date</Form.Label>
          <Form.Control
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
        </Form.Group>
        <HouseholdGroupHouseholdPicker
          effectiveDate={effectiveDate}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          multiple={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <SubmitButton
          label="Add Household"
          isSubmitting={addMember.isPending}
        />
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Form>
  );
};

/**
 * One date field, "Save" and "Cancel", for the remove and edit modals. The
 * date is sent as entered: no day is added or subtracted.
 */
const MembershipDateForm = ({
  controlId,
  label,
  initialDate,
  isSubmitting,
  onSave,
  onHide,
}: {
  controlId: string;
  label: string;
  initialDate: string;
  isSubmitting: boolean;
  onSave: (date: string) => Promise<void>;
  onHide: () => void;
}) => {
  const [date, setDate] = useState(initialDate);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      await onSave(date);
      onHide();
    } catch (err) {
      console.error("Failed to save household group membership", err);
      setSubmitError(failureText(err));
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <Modal.Body>
        {submitError === null ? null : (
          <Alert variant="danger">{submitError}</Alert>
        )}
        <Form.Group className="mb-3" controlId={controlId}>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <SubmitButton isSubmitting={isSubmitting} />
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Form>
  );
};

/** Ends a membership with no end date on an inclusive end date. */
const RemoveHouseholdGroupMemberModal = ({
  householdGroupId,
  member,
  onSaved,
  onHide,
}: {
  householdGroupId: number;
  member: HouseholdGroupMember;
  /** Called after the api accepts the write. */
  onSaved: () => void;
  onHide: () => void;
}) => {
  const removeMember = useRemoveHouseholdGroupMember(
    householdGroupId,
    member.householdId,
  );

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Remove</Modal.Title>
      </Modal.Header>
      <MembershipDateForm
        controlId="household-group-remove-endDate"
        label="End Date"
        initialDate={today()}
        isSubmitting={removeMember.isPending}
        onSave={async (endDate) => {
          await removeMember.mutateAsync({ endDate });
          onSaved();
        }}
        onHide={onHide}
      />
    </Modal>
  );
};

/** Changes the effective date of a membership with no end date. */
const EditHouseholdGroupMemberModal = ({
  householdGroupId,
  member,
  onSaved,
  onHide,
}: {
  householdGroupId: number;
  member: HouseholdGroupMember;
  /** Called after the api accepts the write. */
  onSaved: () => void;
  onHide: () => void;
}) => {
  const changeEffectiveDate = useChangeHouseholdGroupMemberEffectiveDate(
    householdGroupId,
    member.householdId,
  );

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit</Modal.Title>
      </Modal.Header>
      <MembershipDateForm
        controlId="household-group-edit-effectiveDate"
        label="Effective Date"
        initialDate={member.effectiveDate}
        isSubmitting={changeEffectiveDate.isPending}
        onSave={async (effectiveDate) => {
          await changeEffectiveDate.mutateAsync({ effectiveDate });
          onSaved();
        }}
        onHide={onHide}
      />
    </Modal>
  );
};

/**
 * The member households of a group, one row per membership row in the api's
 * order, ended rows included. `endDate` is inclusive (the last day the
 * household is a member) and is shown as stored.
 *
 * In a group with no `dissolvedEffectiveDate`, a household can be added, and
 * each row with no end date can be removed or have its effective date changed.
 * The client does not pre-check the member count or the payer account: the
 * api refuses those writes and the page shows its text.
 */
const HouseholdGroupMembership = ({
  householdGroupId,
  householdGroupName,
  dissolvedEffectiveDate,
  members,
}: {
  householdGroupId: number;
  householdGroupName: string;
  dissolvedEffectiveDate: string | null;
  members: HouseholdGroupMember[];
}) => {
  const [openModal, setOpenModal] = useState<
    | { action: "add" }
    | { action: "remove" | "edit"; member: HouseholdGroupMember }
    | null
  >(null);
  const onHide = () => setOpenModal(null);
  const notifyUpdated = useNotifyHouseholdGroupUpdated(
    householdGroupId,
    householdGroupName,
  );

  // Rows of a dissolved group and ended rows take no write.
  const isOpen = (member: HouseholdGroupMember) =>
    dissolvedEffectiveDate == null && member.endDate == null;

  // The row controls are icons in the cell of the date each one changes, so a
  // row's cells keep the three columns of the read-only table.
  return (
    <div>
      <h3>Member Households</h3>
      <Table>
        <thead>
          <tr>
            <th>Household</th>
            <th>Effective Date</th>
            <th>End Date</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={`${member.householdId}_${member.effectiveDate}`}>
              <td>
                <Link to={`/clients/households/${member.householdId}`}>
                  {member.householdName ?? member.householdId}
                </Link>
              </td>
              <td>
                {member.effectiveDate}
                {isOpen(member) ? (
                  <ActionButton
                    variant="icon"
                    icon="/icons/edit.svg"
                    label="Edit"
                    type="button"
                    className="ms-2"
                    onClick={() => setOpenModal({ action: "edit", member })}
                  />
                ) : null}
              </td>
              <td>
                {member.endDate ?? ""}
                {isOpen(member) ? (
                  <ActionButton
                    variant="icon"
                    icon="/icons/trash.svg"
                    label="Remove"
                    type="button"
                    onClick={() => setOpenModal({ action: "remove", member })}
                  />
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {dissolvedEffectiveDate != null ? null : (
        <Button type="button" onClick={() => setOpenModal({ action: "add" })}>
          Add Household
        </Button>
      )}
      <Modal show={openModal?.action === "add"} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Household</Modal.Title>
        </Modal.Header>
        {openModal?.action === "add" ? (
          <AddHouseholdGroupMemberForm
            householdGroupId={householdGroupId}
            onSaved={notifyUpdated}
            onHide={onHide}
          />
        ) : null}
      </Modal>
      {openModal?.action === "remove" ? (
        <RemoveHouseholdGroupMemberModal
          householdGroupId={householdGroupId}
          member={openModal.member}
          onSaved={notifyUpdated}
          onHide={onHide}
        />
      ) : null}
      {openModal?.action === "edit" ? (
        <EditHouseholdGroupMemberModal
          householdGroupId={householdGroupId}
          member={openModal.member}
          onSaved={notifyUpdated}
          onHide={onHide}
        />
      ) : null}
    </div>
  );
};

export default HouseholdGroupMembership;
